import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { api, fmtUSD, fmtPrice, fmtDate } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import EditTradeDialog from "@/components/EditTradeDialog";

export default function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/admin/trades", { params: { status: "closed" } })
      .then((r) => setTrades(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete this closed trade? The booked P&L of ${fmtUSD(t.pnl)} will be reversed from the client balance.`)) return;
    try {
      // First reverse the pnl by editing to 0, then delete
      await api.patch(`/admin/trades/${t.id}`, { pnl: 0 });
      await api.delete(`/admin/trades/${t.id}`);
      toast.success("Trade deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const totalPnL = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const wins = trades.filter((t) => t.pnl > 0).length;
  const losses = trades.filter((t) => t.pnl < 0).length;
  const winRate = trades.length ? ((wins / trades.length) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-6" data-testid="trade-history-page">
      <div className="grid grid-cols-4 gap-px bg-border">
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Closed Trades</div><div className="font-mono-num text-2xl font-semibold">{trades.length}</div></div>
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Win Rate</div><div className="font-mono-num text-2xl font-semibold">{winRate}%</div></div>
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Wins / Losses</div><div className="font-mono-num text-2xl font-semibold"><span className="text-profit">{wins}</span> / <span className="text-loss">{losses}</span></div></div>
        <div className="bg-card p-5"><div className="label-eyebrow mb-1">Booked P&amp;L</div><div className={`font-mono-num text-2xl font-semibold ${totalPnL >= 0 ? "text-profit" : "text-loss"}`}>{fmtUSD(totalPnL, { signed: true })}</div></div>
      </div>

      <div className="border border-subtle bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-hover border-subtle">
              <TableHead>Client</TableHead>
              <TableHead>Instrument</TableHead>
              <TableHead>Side</TableHead>
              <TableHead className="text-right">Lots</TableHead>
              <TableHead className="text-right">Entry</TableHead>
              <TableHead className="text-right">Close</TableHead>
              <TableHead className="text-right">P&amp;L (USD)</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Closed</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && trades.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No closed trades yet</TableCell></TableRow>}
            {trades.map((t) => (
              <TableRow key={t.id} className="border-subtle hover:bg-surface-hover" data-testid={`closed-trade-${t.id}`}>
                <TableCell className="text-sm">{t.user_name || t.user_id}</TableCell>
                <TableCell className="font-semibold">{t.instrument}</TableCell>
                <TableCell>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.side === "buy" ? "bg-profit/15 text-profit" : "bg-loss/15 text-loss"}`}>
                    {t.side.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono-num">{t.lots.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.entry_price)}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.close_price)}</TableCell>
                <TableCell className={`text-right font-mono-num font-semibold ${t.pnl >= 0 ? "text-profit" : "text-loss"}`}>{fmtUSD(t.pnl, { signed: true })}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(t.opened_at)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(t.closed_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setEditing(t)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`edit-closed-${t.id}`} title="Edit trade">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(t)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`delete-closed-${t.id}`} title="Delete trade">
                      <Trash2 className="w-3.5 h-3.5 text-loss" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EditTradeDialog
        trade={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}
